import { NOISE_MARGIN_DB } from '@muzly/shared';

import { onsetMs } from '../capture';

import type { CaptureSource, LiveSample } from '../capture';

/**
 * CaptureProbe — 측정 화면 위에 뜨는 한 줄짜리 계측 오버레이. 더보기 › 개발자 옵션의
 * 스위치로만 켜진다(`probeFlag.ts`).
 *
 * 여기 있는 넷이 2026-09-10 재조사에서 **서로를 가르지 못해 멈춘 가설들**이다
 * (`.claude/20260910/DEBUG-15-34-53.md` § 계측). 실기기 한 테이크로 답이 나온다:
 *
 * - `src` — 프레임이 정말 네이티브 엔진에서 오나. `mic`/`mock` 이면 그 아래 값은 전부
 *   다른 이야기다(추정기가 다르다).
 * - `floor` — 음성 게이트가 이 방의 조용함을 정했나. `OPEN` 이면 바닥을 못 잡아 엔진
 *   판정만으로 통과시키는 중이고, 그때 암소음이 음으로 그려진다(midi 45~47 실측).
 * - `sm` — 엔진이 표시용 스무딩 값을 실어 오나. `NO` 면 화면이 raw f0 를 그리고, raw 에는
 *   옥타브 억제가 안 걸려 있다.
 *
 * 곁들이는 둘: `v`(발성으로 센 초)와 `faint`(엔진은 들었는데 게이트가 버림) — 집음이 아예
 * 없는 것과 작아서 버려지는 것을 가른다.
 *
 * TestFlight 2차 #67을 가르는 둘(`capture/probeLatch.ts`):
 * - `onset` — 엔진이 처음 들은 때 → 게이트가 첫 목소리를 받아들이기까지(ms). "바로 집음되지
 *   않는다"가 게이트 탓인지 가른다(사용자가 늦게 부른 시간은 안 들어간다). 기준을 넘으면 붉게.
 * - `oct` — 채점 음(raw)과 화면 음(smoothed)이 옥타브로 갈린 프레임 수. 0이 아니면 붉게 —
 *   "라인데 C3로 보인다"에서 두 계열이 갈렸다는 뜻이고, 어느 쪽이 틀렸는지는 화면 음과 대조한다.
 *
 * **프로덕션 코드에 남는 계측이다.** 스위치가 꺼져 있으면 렌더되지 않으므로 평소 화면에
 * 영향이 없고, 다음 회귀 때 배선을 처음부터 다시 하지 않는다.
 */

interface CaptureProbeProps {
  source: CaptureSource | null;
  live: LiveSample;
  /**
   * 노래 테이크의 반주 `play()` 가 거절당한 사유(`SongReferenceAudio`). 없으면 null.
   * 노래 테이크가 아니면 늘 null이라 칸이 조용하다.
   */
  backingPlayError?: string | null;
}

/** 눈에 띄어야 하는 값(= 가설이 살아 있는 쪽)만 붉게. */
const Cell = ({
  label,
  value,
  alert,
}: {
  label: string;
  value: string;
  alert?: boolean;
}) => (
  <span className={alert ? 'text-[#ff5470]' : 'text-white/70'}>
    {label}=<span className="font-bold">{value}</span>
  </span>
);

export const CaptureProbe = ({
  source,
  live,
  backingPlayError = null,
}: CaptureProbeProps) => (
  // 화면 **아래**에 붙인다. `top-0` 은 iOS 상태바(노치) 아래로 깔려 실기기에서 안 보였다
  // (2026-09-16 실기기 보고) — 웹뷰가 edge-to-edge 라 fixed 요소에 safe-area 가 안 붙는다.
  <div
    aria-hidden="true"
    className="pointer-events-none fixed inset-x-0 bottom-0 z-[9999] flex flex-wrap justify-center gap-x-3 gap-y-0.5 bg-black/85 px-2 pb-[env(safe-area-inset-bottom)] pt-1 font-mono text-[10px] leading-tight"
  >
    <Cell label="src" value={source ?? '—'} alert={source !== 'native'} />
    <Cell
      label="floor"
      value={
        live.noiseFloorDbfs == null
          ? 'OPEN'
          : `${live.noiseFloorDbfs.toFixed(1)}`
      }
      alert={!live.floorLocked}
    />
    {/* 게이트 통과선(`floor + NOISE_MARGIN_DB`)과 지금 들어온 rms 를 나란히. 반주가
        방으로 잡히면 `pass` 가 목소리보다 위에 서고, 그 순간이 화면에 보인다. */}
    <Cell
      label="pass"
      value={
        live.noiseFloorDbfs == null
          ? '—'
          : `${(live.noiseFloorDbfs + NOISE_MARGIN_DB).toFixed(1)}`
      }
    />
    <Cell
      label="rms"
      value={live.lastRmsDbfs == null ? '—' : `${live.lastRmsDbfs.toFixed(1)}`}
    />
    <Cell
      label="sm"
      value={live.smoothedSeen ? 'yes' : 'NO'}
      alert={!live.smoothedSeen}
    />
    <Cell label="v" value={`${live.voicedSec.toFixed(1)}s`} />
    {/* 반주가 안 들린 테이크는 채점이 성립하지 않는다. 거절 사유는 여기 말고는 화면
        어디에도 안 뜬다 — 전에는 통째로 삼켜서 아무 데도 안 남았다. */}
    {backingPlayError != null && (
      <Cell label="mr" value={backingPlayError} alert />
    )}
    <Cell
      label="faint"
      value={live.faintAtMs == null ? 'no' : 'yes'}
      alert={live.faintAtMs != null}
    />
    {/* 프레임이 멈춘 이유 — 세션 인터럽션·스트림 끊김은 여기 말고는 화면 어디에도 안 뜬다. */}
    <Cell
      label="int"
      value={String(live.interruptCount)}
      alert={live.interruptCount > 0}
    />
    <Cell
      label="disc"
      value={String(live.discontinuityCount)}
      alert={live.discontinuityCount > 0}
    />
    <Cell label="ev" value={live.lastEvent ?? '—'} />
    <OnsetCell onset={onsetMs(live.probe)} />
    <Cell
      label="oct"
      value={String(live.probe.octaveMismatch)}
      alert={live.probe.octaveMismatch > 0}
    />
  </div>
);

/**
 * 게이트가 이보다 오래 붙잡고 있으면 "바로 집음되지 않는다"로 읽힌다(ms, 미보정). 바닥을 재는
 * 300ms(`CALIBRATION_MS`) 안에 부르기 시작하면 그만큼은 정상으로도 보이므로 그 위에 둔다.
 */
const ONSET_ALERT_MS = 500;

const OnsetCell = ({ onset }: { onset: number | null }) => (
  <Cell
    label="onset"
    value={onset == null ? '—' : `${Math.round(onset)}ms`}
    alert={onset != null && onset > ONSET_ALERT_MS}
  />
);
